import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDependency } from '../../../contexts/DependencyProvider';
import { useViewInfo } from '../../../hooks/UseViewInfo';
import { statusColor } from '../../../utils/helperUtil';
import useBrowserName from '../../../hooks/UseBrowserName';
import useScreenSize from '../../../hooks/UseScreenSize';
import { ToastQueue } from '@adobe/react-spectrum';
import { useAsyncList } from 'react-stately';
import {
  Flex,
  View,
  ProgressCircle,
  Grid,
  TableView,
  TableBody,
  TableHeader,
  Row,
  Cell,
  Column,
  Heading,
  Content,
  ListBox,
  Item,
  IllustratedMessage,
  Link,
  DialogContainer,
  MenuTrigger,
  Menu,
} from '@adobe/react-spectrum';
import NotFound from '@spectrum-icons/illustrations/NotFound';
import FilterComponent from './components/FilterComponent';
import HeaderComponent from './components/HeaderComponent';
import { invokeFinderApp } from '../../../utils/NavigationUtils';
import { ListStatusRequest, ListStatusType } from '../../../services/soap/project/requests/ListStatusRequest';
import { ListFormSettingsRequest } from '../../../services/soap/features/requests/ListFormSettingsRequest';
import { LayoutDefinition } from '../../../infra/protected/Layout/LayoutDefinitions';
import { SoapUtils } from '../../../utils/SoapUtils';
import { GetFormSettingsRequest } from '../../../services/soap/features/requests/GetFormSettingsRequest';
import CardComponent from './components/CardComponent';
import { ComponentFactory } from '../../../components/protected/layout-components/ComponentFactory';
import FilterSearchTextBox from './components/FilterSearchTextBox';
import { FPAData, FPADataFactory } from '../../../infra/protected/FPA/FPAData';
import { usePreloadAssets } from '../../../hooks/UsePreloadAssets';
import { RootState } from '../../../state/store';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { updateAppInfo } from '../../../state/userWebLayout/userWebLayoutSlice';

import { ListItemRequest } from '../../../services/soap/item/requests/ListItemRequest';
import ProductDetailView from './components/ProductDetailView';
import AddUpdateProduct from './components/AddUpdateProduct';
import { GetItemRequest } from '../../../services/soap/item/requests/GetItemRequest';
import { ListItemStatusRequest } from '../../../services/soap/item/requests/ListItemStatusRequest';
import { ListItemPriceListsRequest } from '../../../services/soap/item/requests/ListItemPriceListsRequest';
import { ListItemPricesRequest } from '../../../services/soap/item/requests/ListItemPricesRequest';
import { DeleteItemRequest } from '../../../services/soap/item/requests/DeleteItemRequest';
import './product.css';
import styles from './Product.module.css';
import ProductDetailViewMobile from './components/ProductDetailViewMobile';
import { DeleteConfirmationDialog } from '../finder/components/DeleteConfirmationDialog';
import { useDeviceDetection } from '../../../hooks/UseDeviceDetection';
import { addKey, useKeyboardShortcuts } from '../../../hooks/UseKeyboardShortcuts';

interface Character {
  id: string;
  name: string;
}

export function Product() {
  const browserName = useBrowserName();
  const screenSize = useScreenSize();
  const { t } = useTranslation('products');
  const _t = (key: string) => t(`product_detail.${key}`, { ns: 'products' });
  const _tt = (key: string) => t(`product_common.${key}`, { ns: 'products' });
  const _ttt = (key: string) => t(`table_columns.${key}`, { ns: 'products' });
  const { isMobile } = useViewInfo();
  const { store, projectService, featureService, mainService, itemService } = useDependency();
  const dataStore = useSelector((state: any) => state.userWebLayout.appInfos);
  const selectedApp = useSelector((state: RootState) => state.userWebLayout.selectedAppId);
  const dispatch = useDispatch();
  const { state } = useLocation();
  const [showLoader, setShowLoader] = useState(true);
  const [showLoaderDetail, setShowLoaderDetail] = useState(true);
  const [rowsData, setRowsData] = useState<any>([]);
  const [columnsData, setColumnsData] = useState<any>([]);
  const [pageView, setPageView] = useState<string>('Full');
  const [selectedKeys, setSelectedKeys] = useState<Set<any>>();
  const [activityStatusList, setActivityStatusList] = useState<any>([]);
  const [ProductStatusList, setProductStatusList] = useState<any>([]);
  const [definition, setDefinition] = useState<LayoutDefinition | null>(null);
  const [showMobileDetailView, setShowMobileDetailView] = useState<boolean>(false);
  const [stopLoadData, setStopLoadData] = useState<boolean>(false);
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [selectedDataRow, setSelectedDataRow] = useState<any>({});
  const [isPerson, setIsPerson] = useState<boolean>(false);
  const [listOrganizationFolder, setListOrganizationFolder] = useState<any>([]);
  const [employeesList, setEmployeesList] = useState<any>([]);
  const serviceActivityValue = useRef<string>('');
  const queryParams = useRef<any>({ FILTER: { COL: [] } });
  const totalProductListRec = useRef<number>(0);
  const offSetLimit = useRef<number>(0);
  const dataRef = useRef<any>({});
  const [showAdd, setShowAdd] = useState(false);
  const [addFormType, setAddFormType] = useState<string>('');
  const [isEdit, setIsEdit] = useState(false);
  const [showRelatedCompany, setShowRelatedCompany] = useState<boolean>(false);
  const [params] = useSearchParams();
  const [showCards, setShowCards] = useState<boolean>(true);
  const [priceList, setPriceList] = useState<any[]>([]);
  const [isOpen, setOpen] = useState(false);
  const deviceInfo = useDeviceDetection(isMobile);
  const [showFilter, setshowFilter] = useState<boolean>(false);
  const PAGE_SIZE = 50;
  useEffect(() => {
    if (state.appKey && dataStore[state.appKey]) {
      if (dataStore[state.appKey].pageView) {
        dataRef.current.pageView = dataStore[state.appKey].pageView;
        setPageView(dataStore[state.appKey].pageView);
        return;
      }
    }
  }, [dataStore.appInfos, state.appKey]);

  useEffect(() => {
    (async () => {
      if (params.has('id')) {
        setShowCards(false);
      }
      setShowLoader(true);
      setShowLoaderDetail(true);
      queryParams.current = { FILTER: { COL: [] } };
      serviceActivityValue.current = '';
      setPageView('Full');
      setSearchedValue('');
      Promise.all([getFolderStatus(), getPriceList()]);
    })();
  }, []);

  useEffect(() => {
    if (selectedApp !== '') {
      listProductList.reload();
    }
  }, [selectedApp]);

  useEffect(() => {
    return () => {
      dispatch(
        updateAppInfo({
          key: state.appKey,
          data: {
            pageView: dataRef.current.pageView,
          },
        })
      );
    };

  }, []);

  const getFolderStatus = async () => {
    // const res = await projectService.listFolderStatus2(new ListStatusRequest(store.Server, store.SessionId, ListStatusType.Folder));
    // const statusArray: any = [];
    // if (res.result === 'OK' && res.STATUS.length > 0) {
    //   res?.STATUS.forEach((status: any) => {
    //     const { id, name, color } = status;
    //     let colorString = statusColor(color);
    //     statusArray.push({ id, name, color: colorString });
    //   });
    //   setProductStatusList(statusArray);
    // }
    const statusArray: any[] = [];
    const request = await itemService.listItemStatus(new ListItemStatusRequest(store.Server, store.SessionId));

    if (request.result === "OK") {
      request.ITEM_STATUS.forEach((status: any) => {
        const { id, name } = status;

        // Generate a random number between 1 and 16777215 (0xFFFFFF)
        const randomNumber = Math.floor(Math.random() * 16777215) + 1;

        // Convert it using your statusColor function
        const colorString = statusColor(randomNumber.toString());

        statusArray.push({ id, name, color: colorString });
      });
      setProductStatusList(statusArray);
    }
  };

  const getFormSettings = useCallback(
    async (settingId: string) => {
      setColumnsData([
        { key: 'name', name: _ttt('product_name') },
        { key: 'refId', name: _ttt('refId') },
        { key: 'statusName', name: _ttt('product_status') },
        { key: 'amount', name: _ttt('price'), isNumeric: true },
      ]);
    },
    [selectedApp]
  );

  // const getAddFormSettings = useCallback(
  //   async (settingId: string, formType: string) => {
  //     if (settingId === '0') {
  //       const resFormSettings = await featureService.listFormSettings(new ListFormSettingsRequest(store.Server, store.SessionId));
  //       let filteredData = resFormSettings.SETTING.filter((item: any) => item.formId === formType && item.isDefault == '1');
  //       if (filteredData.length > 0) {
  //         settingId = filteredData[0].id.toString();
  //       }
  //     }
  //     const response = await featureService.getFormSettings(new GetFormSettingsRequest(store.Server, store.SessionId, settingId));
  //     if (response?.result === 'OK' && response?.count > 0) {
  //       const filteredData = response.SETTING[0]?.ITEM?.filter(item => String(item.mandatory) === '1');
  //       if (filteredData.length > 0) {
  //         const processedConfig = filteredData.length > 0 ? filteredData.reduce((acc: any, field: any) => {
  //           acc[field.itemname] = {
  //             visible: field.visibility === '1',
  //             required: field.mandatory === '1',
  //           };
  //           return acc;
  //         }, {}) : {};

  //         setFieldConfig(processedConfig);
  //       } else {
  //         ToastQueue.negative("Form settings are missing", { timeout: 300 });
  //       }
  //     }
  //   },
  //   [selectedApp]
  // );

  const getProductListCount = async () => {
    await getFormSettings('');
    const requestParams = mapFiltersToRequest(queryParams.current.FILTER);
    requestParams.countOnly = true;
    const response = await itemService.listItem(new ListItemRequest(store.Server, store.SessionId, 1, 0, requestParams));
    if (response.result === 'OK') {
      totalProductListRec.current = Number(response.count || 0);
      return totalProductListRec.current;
    }

    totalProductListRec.current = 0;
    return 0;
  };

  const mapFiltersToRequest = (filters: { COL: any[] }) => {
    const requestParams: {
      orderBy?: string;
      countOnly?: boolean;
      type?: string;
      status?: string;
      refId?: string;
      manufacturer?: string;
      searchName?: string;
    } = {
      orderBy: 'name',
    };

    filters.COL.forEach(filter => {
      if (filter.name === 'contains') {
        requestParams.searchName = `${filter.value}`;
      }
      if (filter.name === 'product_code') {
        requestParams.refId = filter.value;
      }
      if (filter.name === 'manufacturer') {
        requestParams.manufacturer = filter.value;
      }
      if (filter.name === 'status') {
        requestParams.status = filter.value;
      }
      if (filter.name === 'type') {
        requestParams.type = filter.value;
      }
    });
    return requestParams;
  };

  const loadProductList = async (append = false) => {
    try {
      if (stopLoadData) {
        return rowsData;
      }
      const requestParams = mapFiltersToRequest(queryParams.current.FILTER);
      setSearchedValue('');
      const response: any = await itemService.listItem(new ListItemRequest(store.Server, store.SessionId, PAGE_SIZE, offSetLimit.current, requestParams));
      let itemList: any[] = [];
      if (response.result === 'OK' && response?.count > 0) {
        if (Array.isArray(response.ITEM)) {
          itemList = response.ITEM;
        } else if (response.ITEM) {
          itemList = [response.ITEM];
        }
        setRowsData((prev: any) => (append ? [...prev, ...itemList] : itemList));
        if (params.has('id')) {
          getLayout(+params.get('id')!);
        } else {
          if (rowsData.length === 0) { getLayout(itemList[0]?.id) }
        }
        return itemList;
      } else {
        if (!append) {
          ToastQueue.info(t('no_results', { ns: 'layout_components' }), { timeout: 50 });
          setRowsData([]);
        }
        return [];
      }
    } finally {
      setShowLoader(false);
    }
  };

  const listProductList = useAsyncList<Character>({
    async load({ cursor, signal }) {
      try {
        if (!selectedApp) {
          return { items: [], cursor: undefined };
        }

        const currentPage = cursor ? parseInt(cursor, 10) : 0;
        const estimatedLoadedCount = currentPage * PAGE_SIZE;
        if (cursor && estimatedLoadedCount >= totalProductListRec.current) {
          return { items: rowsData, cursor: undefined };
        }
        const totalRecords = currentPage === 0 ? await getProductListCount() : totalProductListRec.current;

        if (signal?.aborted) {
        }
        const offset = Math.min(PAGE_SIZE * currentPage, totalRecords ?? 0);

        offSetLimit.current = offset;

        const items = await loadProductList(currentPage > 0);
        const hasMoreItems = offset + (items?.length || 0) < (totalRecords ?? 0);
        const nextCursor = hasMoreItems ? String(currentPage + 1) : undefined;

        return {
          items: items || [],
          cursor: nextCursor,
        };
      } catch (error) {
        console.error('Error loading activity list:', error);
        ToastQueue.negative(t('error_loading_data', { ns: 'layout_components' }) || 'Error loading data', { timeout: 3000 });
        return { items: [], cursor: undefined };
      }finally {
        setShowLoader(false);
        setshowFilter(true);
      }
    },
    async sort({ items, sortDescriptor }) {
      if (!sortDescriptor) return { items };
      const { column, direction } = sortDescriptor;
      return {
        items: [...items].sort((a: any, b: any) => {
          const aValue = a[column];
          const bValue = b[column];

          let result = 0;
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            result = aValue.localeCompare(bValue);
          } else if (aValue < bValue) {
            result = -1;
          } else if (aValue > bValue) {
            result = 1;
          }

          return direction === 'descending' ? -result : result;
        }),
      };
    },

    getKey: item => item.id,
  });

  const reloadProducts = useCallback((filterParams: any) => {
    setStopLoadData(false);
    queryParams.current = filterParams;
    totalProductListRec.current = 0;
    offSetLimit.current = 0;
    setRowsData([]);
    setShowCards(true);
    setShowLoader(true);
    listProductList.reload();
  }, []);

  const handleTableRowClick = (rowId: any): void => {
    if (selectedKeys?.has(rowId.currentKey) || rowId === null) {
      return;
    } else {
      setShowLoaderDetail(true);
      setShowRelatedCompany(false);
      setSelectedKeys(new Set([rowId]));
      setPageView('Full');
      dataRef.current.pageView = 'Full';
      let selectedRow = rowsData.find((item: any) => item.id === rowId);
      if (!selectedRow) {
        setSelectedDataRow({});
        setSelectedKeys(undefined);
        setDefinition(null);
        return;
      }
      setSelectedDataRow(selectedRow);
      getLayout(rowId);
    }
  };

  const handleListRowClick = (rowId: any): void => {
    if (selectedKeys?.has(rowId.currentKey) || rowId.currentKey === null) {
      return;
    } else {
      setShowLoaderDetail(true);
      setShowRelatedCompany(false);
      setSelectedKeys(rowId);
      setPageView('Full');
      dataRef.current.pageView = 'Full';
      let selectedRow = rowsData.find((item: any) => item.id === rowId.currentKey);
      if (!selectedRow) {
        setSelectedDataRow({});
        setSelectedKeys(undefined);
        setDefinition(null);
        return;
      }
      setSelectedDataRow(selectedRow);
      getLayout(rowId.currentKey);
    }
  };

  const handleRowClickMobile = (rowId: any): void => {
    setShowRelatedCompany(false);
    const nameSearchExists = queryParams.current.FILTER.COL.find((col: any) => col.name === 'nameSearch');
    if (nameSearchExists) {
      setSearchedValue(nameSearchExists.value);
    }
    setSelectedKeys(undefined);
    let selectedRow = rowsData.find((item: any) => item.id === rowId.currentKey);
    if (!selectedRow) {
      setSelectedDataRow({});
      setSelectedKeys(undefined);
      setDefinition(null);
      return;
    }
    setSelectedDataRow(selectedRow);
    getLayout(rowId.currentKey);
    setShowMobileDetailView(true);
  };

  const handleViewChange = (view: string) => {
    setPageView(view);
    setShowCards(true);
    dataRef.current.pageView = view;
  };

  const getStatusColor = () => {
    if ((
      selectedItem.statusName === 'Aktivní' ||
      selectedItem.statusName === 'aktivní' ||
      selectedItem.statusName === 'active' ||
      selectedItem.statusName === 'Active')) {
      return '#1976D2';
    } else {
      return '#B0BEC5';
    }
  };

  function extractSingleObject(data: any): any {
    if (typeof data !== 'object' || data === null) {
      return null;
    }
    const keys = Object.keys(data);
    if (keys.some(key => !isNaN(Number(key)))) {
      for (const key of keys) {
        if (!isNaN(Number(key))) {
          return data[key];
        }
      }
    }
    return data;
  }

  const getLayout = async (selectedId: any) => {
    try {
      const ProductResult = await itemService.getItem(new GetItemRequest(store.Server, store.SessionId, selectedId));
      if (ProductResult.result === 'OK') {
        const products = ProductResult.ITEM;
        let result = null;
        if (Array.isArray(products)) {
          result = products.length > 0 ? extractSingleObject(products[0]) : null;
        } else {
          result = products;
        }

        const priceListResponse = await itemService.listItemPrices(new ListItemPricesRequest(store.Server, store.SessionId, selectedId));
        setSelectedDataRow(result);
        setSelectedItem(result);
        setSelectedKeys(new Set([result.id]));
        setShowLoaderDetail(false);
        return result;
      }
    } catch (err) {
      console.error('getlayout error: ', err);
    } finally {
      setShowLoaderDetail(false);
    }
  };

  const Loader = () => (
    <Flex width="100%" justifyContent="center" marginTop={10}>
      <ProgressCircle aria-label="Loading…" isIndeterminate />
    </Flex>
  );

  const NoResultsMessage = () => (
    <Flex direction="row" width="100%" alignContent="center" justifyContent="center">
      <IllustratedMessage>
        <NotFound />
        <Heading>{t('no_results', { ns: 'layout_components' })}</Heading>
        <Content>{t('try_another_search', { ns: 'layout_components' })}</Content>
      </IllustratedMessage>
    </Flex>
  );

  const getHeight = (baseHeight: number) => {
    if (browserName === 'Chrome') {
      return `${baseHeight - 120}px`;
    } else if (browserName === 'Safari') {
      return `${baseHeight - 120}px`;
    } else {
      return `${baseHeight - 120}px`;
    }
  };

  const getWidth = () => {
    return `95%`;
  };

  const detailStyle = {
    height: getHeight(screenSize.height - 20),
  };

  const cardStyle = {
    height: getHeight(screenSize.height),
    width: getWidth(),
    top: 0,
  };

  const filterStyle = {
    height: getHeight(screenSize.height + 60),
    overflow: 'hidden auto',
  };

  const handleAddNew = () => {
    setShowAdd(true);
  };

  const handleAddNewClose = () => {
    setShowAdd(false);
    setIsEdit(false);
    setAddFormType('');
  };

  const handleEditClick = (data: boolean) => {
    if (data) {
      setIsEdit(data);
      setShowAdd(true);
    }
  };

  const refreshDetailView = async (ProductId: any) => {
    setShowLoaderDetail(true);
    setSelectedKeys(new Set([ProductId]));
    const p = await getLayout(ProductId);
    const index = listProductList.items.findIndex((row: any) => row.id === ProductId);
    if (index !== -1) {
      listProductList.items[index] = { ...listProductList.items, ...p };
    }
  };

  const handleRefreshAddNewProduct = async (ProductId: any) => {
    setShowLoaderDetail(true);
    setSelectedKeys(new Set([ProductId]));
    const Product = await getLayout(ProductId);
    const data = [...rowsData];
    setRowsData([...data, Product]);
    listProductList.prepend(Product);
  };

  const handleProductDelete = async (data: any) => {
    const deleteProduct = await itemService.deleteItem(new DeleteItemRequest(store.Server, store.SessionId, data.id, {}));
    if (deleteProduct.result === 'OK') {
      setOpen(false);
      ToastQueue.positive('Product deleted successfully...', { timeout: 3000 });
      setSelectedItem(null);
      setSelectedDataRow({});
      setSelectedKeys(undefined);
      listProductList.reload();
    } else {
      ToastQueue.negative(deleteProduct.EXCEPTION.message, { timeout: 3000 });
    }
  };



  const getPriceList = async () => {
    const response = await itemService.listItemPriceLists(new ListItemPriceListsRequest(store.Server, store.SessionId, {}));
    if (response.result === "OK") {
      setPriceList(response.priceList || []);
    }
  }

  useKeyboardShortcuts([...addKey('c', { ctrl: true, shift: true }, () => handleAddNew())], true);

  const shouldUseDesktopLayout = !deviceInfo.shouldUseMobileLayout() || ((deviceInfo.isTablet || deviceInfo.isIPad) && deviceInfo.orientation === 'landscape');

  const isTablet = deviceInfo.isTablet || deviceInfo.isIPad;

const gridAreas = isTablet
  ? pageView === 'Full'
    ? (['header header', 'filter filter', 'card detail'] as any)
    : (['header header', 'filter filter', 'detail detail'] as any)
  : pageView === 'Full'
    ? (['filter header header', 'filter card detail'] as any)
    : (['filter header header', 'filter detail detail'] as any);

// IMPORTANT: areas must match number of columns.
// Tablet/iPad => 2 columns ALWAYS
// Desktop     => 3 columns ALWAYS
const gridColumns = isTablet
  ? {
      base: ['35%', '65%'],
      L: ['35%', '65%'],
    }
  : {
      base: ['245px', '28%', 'auto'],
      L: ['245px', '28%', 'auto'],
    };

const gridRows = isTablet ? ['auto', 'auto', '1fr'] : ['auto', '1fr'];

  return (
    <>
      {!shouldUseDesktopLayout ?
        (
          <>
            {!showMobileDetailView && showCards && (
              <>
                <FilterComponent onFilterChange={reloadProducts} filterParams={queryParams.current} priceList={priceList} />
                <View position={'relative'} width={'100%'} height={'100%'} UNSAFE_className={styles.card_list_parent}>
                  <Flex direction={'column'} position={'absolute'} width={'100%'} top={'72px'} left={'0%'} alignItems={'start'} justifyContent={'start'} gap={'12px'} maxHeight={'100vh'}>
                    {showLoader ? (
                      <Loader />
                    ) : (
                      <ListBox
                        id="ProductCardsMobile"
                        selectionMode="single"
                        onSelectionChange={handleRowClickMobile}
                        onLoadMore={listProductList.items.length > 40 ? listProductList.loadMore : undefined}
                        marginStart={'18px'}
                        maxHeight={'100vh'}
                        minHeight={'130px'}
                        defaultSelectedKeys={selectedKeys}
                        aria-label="Cards Mobile"
                        items={listProductList.items}
                        isLoading={listProductList.isLoading}
                        width={'calc(100% - 32px)'}
                      >
                        {item => (
                          <Item key={item.id} textValue={item.name}>
                            <CardComponent item={item} activityStatusList={activityStatusList} />
                          </Item>
                        )}
                      </ListBox>
                    )}
                  </Flex>
                  <Flex
                    direction={'column'}
                    position={'absolute'}
                    width={'calc(100% - 32px)'}
                    top={'16px'}
                    right={'16px'}
                    left={'16px'}
                    alignItems={'start'}
                    justifyContent={'start'}
                    UNSAFE_className={styles.search_parent}
                  >
                    <Flex direction={'row'} alignSelf={'stretch'} height={'40px'} flexShrink={0} alignItems={'center'} justifyContent={'end'} gap={'8px'} UNSAFE_className={styles.search_field}>
                      <FilterSearchTextBox onSearchChange={reloadProducts} filterParams={queryParams.current} serachedText={searchedValue} />
                      <i className={`${styles.icon_search} bi bi-search`}></i>
                    </Flex>
                  </Flex>
                  <Flex
                    direction={'row'}
                    position={'fixed'}
                    right={'16px'}
                    bottom={'95px'}
                    height={'48px'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    gap={'8px'}
                    UNSAFE_className={styles.add_new_parent}
                  >
                    <Link onPress={handleAddNew} isQuiet aria-label="Actions" UNSAFE_className={styles.add_new_btn}>
                      <i className="bi bi-plus fs-5">
                        <View UNSAFE_className={styles.add_new_btn_text}>{_tt('button_add_new')}</View>
                      </i>
                    </Link>
                  </Flex>
                </View>
              </>
            )}

            {(showMobileDetailView || !showCards) && (
              <>
                <View id="top" position={'relative'} width={'100%'} height={'64px'} overflow={'hidden'} UNSAFE_className={styles.layout_header_parent}>
                  <Flex gap={'size-150'} position={'relative'} top={'calc(50% - 15px)'} left={'15px'} height={'30px'} UNSAFE_className={styles.layout_header_btn_back}>
                    <Link
                      onPress={() => {
                        setStopLoadData(true);
                        setShowMobileDetailView(false);
                        setShowCards(true);
                      }}
                    >
                      <i className="bi bi-chevron-left fs-5"></i>
                    </Link>
                    <span>{_t("product_details")}</span>
                  </Flex>
                </View>
                <Flex direction="column" alignItems={'start'} justifyContent={'center'} UNSAFE_className={styles.detailsHolder} UNSAFE_style={{ padding: '0px', paddingTop: '0px' }}>
                  {selectedItem && (
                    <>
                      {showLoader ? (
                        <Loader />
                      ) : (
                        <>
                          <View UNSAFE_className={styles.service_status_bar} marginBottom={'5px'} marginTop={'0px'} UNSAFE_style={{ backgroundColor: getStatusColor() }}></View>
                          <ProductDetailViewMobile
                            product={selectedItem}
                            productStatusList={ProductStatusList}
                            onEdit={handleEditClick}
                            onDelete={() => setOpen(true)}
                          />
                        </>
                      )}
                    </>
                  )}
                </Flex>
              </>
            )}
          </>
        ) :
        (showCards ? (
          <Grid
    areas={gridAreas}
    columns={gridColumns}
    rows={gridRows}
    height="100%"
    width="100%"
    // Avoid clipping header UI; if you MUST keep hidden, keep it but note dropdowns can clip.
    UNSAFE_style={{
      backgroundColor: '#f5f6f9',
      padding: '0px',
      margin: '0px',
      overflow: 'hidden auto',
    }}
  >
    {!(deviceInfo.isTablet || deviceInfo.isIPad) && (
      <View gridArea="filter">
        {showFilter ? (
          <FilterComponent
            onFilterChange={reloadProducts}
            filterParams={queryParams.current}
            filterStyle={filterStyle}
            priceList={priceList}
          />
        ) : (
          <Flex
            height={'100%'}
            justifyContent={'center'}
            alignItems={'center'}
            UNSAFE_style={{ backgroundColor: 'white' }}
          >
            <Loader />
          </Flex>
        )}
      </View>
    )}

    {/* ✅ Header: removed height:'100%' which can collapse on auto rows in some mid widths */}
    <View width={'100%'} gridArea="header">
      <HeaderComponent
        onPageViewChange={handleViewChange}
        autoChanged={pageView}
        filterParams={queryParams.current}
        onSearchChange={reloadProducts}
        fpaTypeCategory={serviceActivityValue.current}
        onAddNew={handleAddNew}
      />
    </View>

    {(deviceInfo.isTablet || deviceInfo.isIPad) && (
      <View
        gridArea="filter"
        UNSAFE_style={{
          height: '60px',
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <FilterComponent
          onFilterChange={reloadProducts}
          filterParams={queryParams.current}
          filterStyle={filterStyle}
          priceList={priceList}
        />
      </View>
    )}

    {pageView == 'Full' ? (
      <>
        {showLoader ? (
          <Loader />
        ) : (
          <>
            <View gridArea="card" UNSAFE_style={{ backgroundColor: '#f5f6f9', overflow: 'auto' }}>
              {rowsData?.length > 0 ? (
                <ListBox
                  autoFocus
                  id="productCards"
                  selectionMode="single"
                  onSelectionChange={handleListRowClick}
                  onLoadMore={listProductList.items.length > 45 ? listProductList.loadMore : undefined}
                  marginStart={'15px'}
                  minHeight={'130px'}
                  defaultSelectedKeys={selectedKeys}
                  selectedKeys={selectedKeys}
                  aria-label="Product Cards"
                  items={listProductList.items}
                  isLoading={listProductList.isLoading}
                  UNSAFE_style={{ ...cardStyle, marginTop: 13 }}
                >
                  {(item) => (
                    <Item key={item.id} textValue={item.name}>
                      <CardComponent item={item} activityStatusList={activityStatusList} />
                    </Item>
                  )}
                </ListBox>
              ) : (
                <View marginTop={'13px'}>
                  <NoResultsMessage />
                </View>
              )}
            </View>

            <View
              gridArea="detail"
              backgroundColor={'static-white'}
              borderRadius={'large'}
              padding={'size-100'}
              paddingTop={'0'}
              marginTop={'17px'}
              UNSAFE_style={detailStyle}
              width={'98%'}
              UNSAFE_className={styles.detailView}
            >
              {showLoaderDetail ? (
                <Loader />
              ) : (
                <View
                  height="100%"
                  UNSAFE_className={styles.detailsHolder}
                  UNSAFE_style={{ overflow: 'hidden auto' }}
                  marginStart={'10px'}
                >
                  {selectedItem && (
                    <>
                      {rowsData?.length > 0 ? (
                        <>
                          <View
                            UNSAFE_className={styles.service_status_bar}
                            UNSAFE_style={{ backgroundColor: getStatusColor() }}
                          />
                          <View height={'2px'}>&nbsp;</View>
                          <ProductDetailView
                            product={selectedItem}
                            productStatusList={ProductStatusList}
                            onEdit={handleEditClick}
                            onDelete={() => setOpen(true)}
                          />
                        </>
                      ) : (
                        ''
                      )}
                    </>
                  )}
                </View>
              )}
            </View>
          </>
        )}
      </>
    ) : (
      <View gridArea="detail">
        {showLoader ? (
          <Loader />
        ) : (
          <View
            position={'relative'}
            overflow={'auto'}
            height={'100%'}
            width={'100%'}
            UNSAFE_className={styles.service_activities}
          >
            {columnsData?.length > 0 && rowsData?.length > 0 ? (
              <TableView
                UNSAFE_className="tblLayoutComponent"
                aria-label="Products"
                selectionMode="single"
                defaultSelectedKeys={selectedKeys}
                selectionStyle="highlight"
                width={{ base: '100%', L: '100%', M: '1100px' }}
                minHeight={'100px'}
                maxHeight={{ base: '100vh', L: '625px' }}
                onAction={handleTableRowClick}
              >
                <TableHeader columns={columnsData}>
                  {(column: any) => (
                    <Column showDivider key={column.key} align={column.key === 'amount' ? 'end' : undefined}>
                      {column.name}
                    </Column>
                  )}
                </TableHeader>

                <TableBody
                  onLoadMore={listProductList.items.length > 45 ? listProductList.loadMore : undefined}
                  items={listProductList.items}
                  loadingState={listProductList.loadingState}
                >
                  {(item: any) => (
                    <Row>
                      {(columnKey) => (
                        <Cell>
                          {(() => {
                            if (columnKey === 'amount') {
                              return item[columnKey] && item[columnKey] > 0
                                ? `${item[columnKey]} ${item['currencyName'] ? item['currencyName'] : ''}`
                                : '0.00';
                            } else {
                              return item[columnKey];
                            }
                          })()}
                        </Cell>
                      )}
                    </Row>
                  )}
                </TableBody>
              </TableView>
            ) : (
              <NoResultsMessage />
            )}
          </View>
        )}
      </View>
    )}
  </Grid>
        ) : (
          <Grid
            areas={pageView == 'Full' ? ['detail', 'detail'] : ['detail', 'detail']}
            columns={{ base: ['100%'], L: ['100%'] }}
            rows={['auto', '1fr']}
            height="100%"
            width="100%"
            UNSAFE_style={{ backgroundColor: '#f5f6f9', padding: '10px', margin: '0px', overflow: 'hidden' }}
          >
            {pageView == 'Full' ? (
              <>
                {showLoader ? (
                  <Loader />
                ) : (
                  <>
                    <View
                      gridArea="detail"
                      backgroundColor={'static-white'}
                      borderRadius={'large'}
                      UNSAFE_style={detailStyle}
                      width={'100%'}
                      UNSAFE_className={styles.detailView}
                    >
                      {showLoaderDetail ? (
                        <Loader />
                      ) : (
                        <View height="100%" UNSAFE_className={styles.detailsHolder} UNSAFE_style={{ overflow: 'hidden auto' }} marginStart={'10px'}>
                          {selectedItem && (
                            <>
                              {rowsData?.length > 0 ? (
                                <>
                                  <View UNSAFE_className={styles.service_status_bar} UNSAFE_style={{ backgroundColor: selectedItem.statusColor }}></View>
                                  <View height={'2px'}>&nbsp;</View>
                                  <ProductDetailView
                                    product={selectedItem}
                                    productStatusList={ProductStatusList}
                                    onEdit={handleEditClick}
                                    onDelete={() => setOpen(true)}
                                    handleBackClick={()=> setShowCards(true)}
                                  />
                                </>
                              ) : (
                                ''
                              )}
                            </>
                          )}
                        </View>
                      )}
                    </View>
                  </>
                )}
              </>
            ) : (
              <View gridArea="detail">
                {showLoader ? (
                  <Loader />
                ) : (
                  <View position={'relative'} overflow={'auto'} height={'100%'} width={'100%'} UNSAFE_className={styles.service_activities}>
                    {columnsData?.length > 0 && rowsData?.length > 0 ? (
                      <TableView
                        UNSAFE_className="tblLayoutComponent"
                        aria-label="Products"
                        selectionMode="single"
                        defaultSelectedKeys={selectedKeys}
                        selectionStyle="highlight"
                        width={{ base: '100%', L: '100%', M: '1100px' }}
                        minHeight={'100px'}
                        maxHeight={{ base: '100vh', L: '625px' }}
                        onAction={handleTableRowClick}
                      >
                        <TableHeader columns={columnsData}>
                          {(column: any) => (
                            <Column showDivider key={column.key} align={column.key === 'amount' ? 'end' : undefined}>
                              {column.name}
                            </Column>
                          )}
                        </TableHeader>
                        <TableBody
                          onLoadMore={listProductList.items.length > 45 ? listProductList.loadMore : undefined}
                          items={listProductList.items}
                          loadingState={listProductList.loadingState}
                        >
                          {(item: any) => (
                            <Row>
                              {columnKey => (
                                <Cell>
                                  {(() => {
                                    if (columnKey === 'amount') {
                                      return item[columnKey] && item[columnKey] > 0
                                        ? `${item[columnKey]} ${item['currencyName'] ? item['currencyName'] : ''}`
                                        : '0.00';
                                    } else {
                                      return item[columnKey];
                                    }
                                  })()}
                                </Cell>
                              )}
                            </Row>
                          )}
                        </TableBody>
                      </TableView>
                    ) : (
                      <NoResultsMessage />
                    )}
                  </View>
                )}
              </View>
            )}
          </Grid>
        )
        )
      }
      <DialogContainer onDismiss={handleAddNewClose}>
        {showAdd && <AddUpdateProduct
          product={selectedItem}
          isEdit={isEdit}
          onClose={handleAddNewClose}
          refreshDetailView={refreshDetailView}
          handleRefreshAddNewProduct={handleRefreshAddNewProduct} />
        }
      </DialogContainer>
      <DeleteConfirmationDialog
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        onConfirm={() => handleProductDelete(selectedItem)}
        itemName={selectedItem?.name}
      />
    </>
  );
}
